import { useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import {
  StatusExpandCard,
  Grid,
  Typography,
  Stack,
  Divider,
  useFinanceableTheme,
  IconButton,
  Tooltip,
} from "@financeable-com-au/financeable-ui";
import { DefaultRatesFees } from "@/components/LenderProductCard/DefaultRatesFees";
import { LenderCardDownloadSubmitActions } from "@/components/LenderProductCard/LenderCardDownloadSubmitActions/LenderCardDownloadSubmitActions";
import { LenderCardQuoteSubmitActions } from "@/components/LenderProductCard/LenderCardQuoteSubmitActions/LenderCardQuoteSubmitActions";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { CriteriaProgress } from "@/components/LenderProductCard/CriteriaProgress";
import { FieldProgress } from "@/components/LenderProductCard/FieldProgress";
import { useLenderCardCriteria } from "@/components/LenderProductCard/hooks";
import {
  ApplicationFormSlice,
  ApplicationState,
  ProductsQuote,
  UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import colors from "@/style/colors";

const LenderCardCondensed = () => {
  const {
    product,
    isSingleLenderProduct,
    lenderCard: { repayments, rate },
  } = useLenderCardContext();
  const theme = useFinanceableTheme();
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const { isQuoteOpen } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const isApplication = !isQuoteOpen;

  // If product is undefined, return null.
  if (!product) return null;

  const { logo, lender, productName } = product;
  const { criteriaStatus, criteraList } = useLenderCardCriteria();

  const isProductQuoteExists =
    application?.productsQuotes &&
    application?.productsQuotes.length > 0 &&
    application?.productsQuotes.find((pq) => pq.lender === product?.lender);

  const productsQuote = isProductQuoteExists
    ? (application?.productsQuotes?.find((pq) => pq.lender === product?.lender) as ProductsQuote)
    : null;

  return (
    <Stack
      style={{
        ...(isProductQuoteExists &&
          productsQuote && {
            border: "2px solid",
            borderBottom: "3px solid",
            borderRadius: "8px",
            borderColor: theme.palette.primary[200],
          }),
      }}
    >
      <StatusExpandCard status={criteriaStatus} isCondensed>
        <Grid container columnSpacing={4}>
          <Grid
            xs={3}
            borderRight={`1px solid ${theme.palette.neutral[200]}`}
            display="flex"
            flexDirection="row"
            alignItems="center"
            flexWrap="wrap"
          >
            {logo ? (
              <>
                {isProductQuoteExists && (
                  <Typography
                    level="body-xs"
                    component="span"
                    bold
                    style={{
                      position: "absolute",
                      backgroundColor: theme.palette.primary[200],
                      padding: "0 10px",
                      color: colors.white,
                      margin: "-90px 0 0 -17px",
                      width: "fit-content",
                      borderRadius: "6px 0 10px 0",
                    }}
                  >
                    Quote
                  </Typography>
                )}
                <img
                  width="100%"
                  height="auto"
                  style={{ objectFit: "contain", maxWidth: "75px" }}
                  src={logo}
                  alt={lender}
                />
              </>
            ) : (
              <Typography level="body-sm">{lender}</Typography>
            )}
          </Grid>
          <Grid xs={9} display="flex" flexDirection="column" rowGap={3}>
            {isSingleLenderProduct && (
              <Stack flexDirection="row" columnGap={1} alignItems="center">
                <Typography level="body-lg" bold component="h3" textColor="neutral.900">
                  {lender}
                </Typography>
                <Typography level="body-sm">({productName})</Typography>
              </Stack>
            )}
            <Stack flexDirection="row" flexWrap="nowrap" justifyContent="space-between">
              {repayments.monthlyRange ? (
                <Typography level="body-sm" component="h4" bold textColor="neutral.900">
                  {repayments.monthlyRange.min.formattedValue} - {repayments.monthlyRange.max.formattedValue}
                </Typography>
              ) : (
                <Typography level={isSingleLenderProduct ? "title-md" : "title-sm"} component="h4">
                  {repayments.monthly.formattedValue}
                </Typography>
              )}
              {!isSingleLenderProduct && (
                <>
                  <Divider orientation="vertical" />
                  <Stack display="flex" flexDirection="row" alignItems="center" columnGap={1}>
                    {rate.defaultRange ? (
                      <Typography level="body-sm" component="h4" bold textColor="neutral.900" textAlign="right">
                        {rate.defaultRange.min.formattedValue} - {rate.defaultRange.max.formattedValue}
                      </Typography>
                    ) : (
                      <Typography level="title-sm" component="h4">
                        {rate.default.formattedValue}
                      </Typography>
                    )}
                    {/* Tooltip for product disclaimer. */}
                    {product?.disclaimer && (
                      <Tooltip
                        placement="left-start"
                        style={{ maxWidth: "300px" }}
                        title={
                          <Stack display="flex" flexDirection="row" alignItems="flex-start" justifyContent="flex-end">
                            <Typography level="body-sm">{product?.disclaimer}</Typography>
                          </Stack>
                        }
                      >
                        <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline" intent="neutral">
                          <InfoIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
            <div>
              <CriteriaProgress />
              {isApplication && <FieldProgress />}
            </div>
          </Grid>
        </Grid>
        {isSingleLenderProduct && (
          <Stack flexDirection="column" mt={5} rowGap={5}>
            <DefaultRatesFees />
            <LenderCardDownloadSubmitActions centerActions />
            <LenderCardQuoteSubmitActions centerActions />
            <Divider />
            <Stack>
              <Typography level="title-sm" component="h5" mb={3}>
                Criteria
              </Typography>
              <Stack flexDirection="column" rowGap={1}>
                {criteraList.map(({ label, icon }) => (
                  <Stack flexDirection="row" alignItems="center" columnGap={3} key={label}>
                    {icon}
                    <Typography level="body-xs">{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        )}
      </StatusExpandCard>
    </Stack>
  );
};

export { LenderCardCondensed };
